import styled from "@emotion/styled";
import { ComponentPropsWithoutRef, Fragment, ReactNode } from "react";

export type ActionToolbarItem = {
  key: string;
  node: ReactNode;
};

/**
 * Items inside the same group are rendered adjacent with no divider.
 * Dividers are inserted only between groups.
 */
export type ActionToolbarGroup = {
  key: string;
  items: ActionToolbarItem[];
};

type ActionToolbarProps = Omit<ComponentPropsWithoutRef<"div">, "children"> & {
  groups: ActionToolbarGroup[];
};

export const ActionToolbar = ({ groups, ...props }: ActionToolbarProps) => {
  const nonEmptyGroups = groups.filter(group => group.items.length > 0);

  return (
    <StyledToolbar {...props}>
      {nonEmptyGroups.map((group, groupIndex) => (
        <Fragment key={group.key}>
          <StyledToolbarGroup>
            {group.items.map(item => (
              <Fragment key={item.key}>{item.node}</Fragment>
            ))}
          </StyledToolbarGroup>
          {groupIndex < nonEmptyGroups.length - 1 ? (
            <StyledToolbarDivider data-test-id="action-toolbar-divider" />
          ) : null}
        </Fragment>
      ))}
    </StyledToolbar>
  );
};

/** @deprecated Use ActionToolbarItem */
export type ImageActionToolbarItem = ActionToolbarItem;

/** @deprecated Use ActionToolbarGroup */
export type ImageActionToolbarGroup = ActionToolbarGroup;

/** @deprecated Use ActionToolbar */
export const ImageActionToolbar = ActionToolbar;

const StyledToolbar = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.spacing["1"]} ${({ theme }) => theme.spacing["2"]};
  background: ${({ theme }) => theme.colors.background.toolbar};
  background-clip: padding-box;
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  box-shadow: ${({ theme }) => theme.shadows.surface};
  border-radius: ${({ theme }) => theme.borderRadius.large};
`;

const StyledToolbarGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledToolbarDivider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.spacing["5"]};
  height: ${({ theme }) => theme.spacing["5"]};
  flex-shrink: 0;

  &::after {
    content: "";
    width: 1px;
    height: 100%;
    background: ${({ theme }) => theme.colors.border.ghost};
  }
`;
